import { Options } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useTheme } from 'styled-components';
import HttpStatus from '../../enums/httpStatus';
import ApiCore from '../../services/utilities/core';
import { selectMenuZIndex } from '../../styles/global';
import * as s from './styles';

type DataMapper<InputType, OutputType> = {
  label: (item: InputType) => string;
  value: (item: InputType) => OutputType;
};

type Props = {
  reqResponseToOption: ((data: any) => Options<any>) | { mapper: DataMapper<any, any> };
  request: ApiCore;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  cacheUniqs?: any[];
  query?: string;
  className?: string;
  id?: string;
  optionsDirection?: 'top' | 'bottom';
  isMulti?: boolean;
  fixedOptions?: { label: string; value: any }[];
  debounceTimeout?: number;
};

const SelectAsyncJSX = ({
  reqResponseToOption,
  request,
  value,
  onChange,
  placeholder,
  isRequired,
  cacheUniqs,
  disabled = false,
  query = '',
  className = '',
  id = '',
  optionsDirection = 'bottom',
  isMulti = false,
  fixedOptions,
  debounceTimeout = 1300,
}: Props) => {
  const theme = useTheme();

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      background: theme.colors.surface,
      color: theme.colors.onSurface,
      border: `1px solid ${theme.colors.onBackground}50`,
      cursor: 'pointer',
      minHeight: '30px',
    }),
    menu: (base: any) => ({
      ...base,
      background: theme.colors.surface,
      color: theme.colors.onSurface,
      zIndex: selectMenuZIndex,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: selectMenuZIndex,
    }),
    menuList: (base: any) => ({
      ...base,
      background: theme.colors.surface,
      color: theme.colors.onSurface,
    }),
    option: (base: any, state: any) => ({
      ...base,
      background: state.isFocused ? theme.colors.primary : theme.colors.surface,
      color: state.isFocused ? theme.colors.onPrimary : theme.colors.onSurface,
      cursor: 'pointer',
      '&:active': {
        background: theme.colors.primary,
        color: theme.colors.onPrimary,
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: theme.colors.onSurface,
    }),
    multiValue: (base: any) => ({
      ...base,
      background: theme.colors.primary,
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: theme.colors.onPrimary,
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: theme.colors.onPrimary,
      ':hover': {
        background: theme.colors.primary,
        color: theme.colors.onPrimary,
        cursor: 'pointer',
      },
    }),
  };

  const loadOptions = async (search: any, _loadedOptions: any, { page }: any) => {
    let options: Options<any> = [];
    let hasMore = true;

    request.query = `?search=${search}&page=${page}`;

    if (query.length > 0) {
      request.query += `&${query}`;
    }

    const res = await request.index();

    if (res.status === HttpStatus.OK) {
      const { data }: any = res.data;
      if (data.length >= 1) {
        if (typeof reqResponseToOption === 'function') {
          // Caso clássico: função recebendo dados e retornando options
          options = reqResponseToOption(data);
        } else if (reqResponseToOption && 'mapper' in reqResponseToOption) {
          // Caso novo: usando mapper para extrair label e value
          const mapper = reqResponseToOption.mapper;
          options = data.map((item: any) => ({
            label: mapper.label(item),
            value: mapper.value(item),
          }));
        }
      }
    }

    if (options.length === 0) {
      hasMore = false;
    }

    const handlePage = (_page: any) => (_page !== undefined ? _page : 1);

    if (fixedOptions) {
      options = [...fixedOptions, ...options];
    }

    return {
      options,
      hasMore,
      additional: {
        page: handlePage(page) + 1,
      },
    };
  };

  const getOptionLabel = (option: any): any => {
    if (!isMulti) {
      return <span style={{ display: 'flex', alignItems: 'center' }}>{option.label}</span>;
    }

    let checked = false;

    if (value && value.some((selectedOption: any) => selectedOption.value === option.value)) {
      checked = true;
    }

    return (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <input type="checkbox" checked={checked} onChange={() => null} />
        {option.label}
      </span>
    );
  };

  return (
    <s.Container
      disabled={disabled}
      borderAlert={isRequired && value?.value === undefined}
      className={className}
    >
      <AsyncPaginate
        value={value}
        onChange={onChange}
        className="multi-select-async"
        menuPlacement={optionsDirection}
        loadOptions={loadOptions}
        additional={{
          page: 1,
        }}
        debounceTimeout={debounceTimeout}
        isDisabled={disabled}
        placeholder={placeholder}
        cacheUniqs={cacheUniqs}
        maxMenuHeight={150}
        id={id}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        getOptionLabel={getOptionLabel}
        isClearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectStyles}
      />
    </s.Container>
  );
};

SelectAsyncJSX.defaultProps = {
  disabled: false,
  placeholder: '',
  isRequired: false,
  cacheUniqs: [],
  query: '',
  className: '',
  id: '',
  optionsDirection: 'bottom',
  isMulti: false,
  debounceTimeout: 1300,
};

export default SelectAsyncJSX;
